<template>
  <div
    v-if="!isMobile"
    class="search-board-container py-3 px-4 pr-1 electron-no-drag"
    @click="focusInput"
  >
    <div
      class="search-input flex items-center rounded border border-solid border-dividers-default bg-background-default"
    >
      <div class="search-input__icon search-input__icon--left ml-2 box-border usn relative">
        <em
          class="bg-icon-default m-auto block h-4 w-4"
          :style="getCustomIconStyle(iconPath('search-alternative'))"
        />
      </div>
      <div class="search-input__control ml-2">
        <input
          ref="searchInput"
          v-model="searchQuery"
          :placeholder="`${translate('search')} ${translate('cards').toLowerCase()}...`"
          class="search-input__field border-0 bg-transparent text-text-default placeholder:text-text-placeholder border-box focus:outline-none text-sm"
          @input="handleInput"
          @keyup.enter="handleEnter"
          @keyup.esc="handleEsc"
        />
      </div>
      <div class="search-input__dot w-2 h-2 mr-4 pointer-events-none">
        <div
          v-show="searchQuery"
          class="rounded-full w-2 h-2 bg-blue-500"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onUnmounted, watch, onMounted } from 'vue';
import useCustomIcon from '@/utilities/composables/useCustomIcon';
import { globalState } from '@/utilities/globalState';

const { iconPath, getCustomIconStyle } = useCustomIcon();

const emit = defineEmits(['input', 'enter', 'clear']);

const searchInput = ref<HTMLInputElement | null>(null);
const searchQuery = ref('');
let debounceTimeout: NodeJS.Timeout | null = null;
let isInternalUpdate = false;

const handleInput = () => {
  // Debounce the global state update
  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
  }
  isInternalUpdate = true;
  debounceTimeout = setTimeout(() => {
    globalState.searchBoardQuery = searchQuery.value;
    isInternalUpdate = false;
  }, 200);

  emit('input', searchQuery.value);
};

// Watch for changes in globalState.searchBoardQuery
watch(
  () => globalState.searchBoardQuery,
  (newValue) => {
    if (!isInternalUpdate) {
      searchQuery.value = newValue;
    }
  }
);

const handleEnter = () => {
  emit('enter', searchQuery.value);
};

const handleEsc = () => {
  searchQuery.value = '';
  emit('clear');
};

const focusInput = () => {
  searchInput.value?.focus();
};

onMounted(() => {
  globalState.searchBoardQuery = '';
});

onUnmounted(() => {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
  }
  globalState.searchBoardQuery = '';
});
</script>

<style lang="scss">
.search-input {
  background-color: var(--importantEl);

  .search-input__field {
    &:focus {
      outline: none;
    }
  }
}
</style>
